/**
 * JsonDenormalizer.js
 *
 * This class transforms data from a JSON API standard response
 * into a more user-friendly structure with nested objects to
 * represent relationships.
 *
 * Haven't found any existing library that will also
 * allow the execution of our UI-side transformers.
 */

import _ from 'lodash/lodash'
import { DataTransformer } from '@/helpers/orders/data.transformer.js'
import { FormTransformer } from '@/helpers/forms/form.transformer.js'
import { FormTypeTransformer } from '@/helpers/forms/form-type.transformer.js'
import { UserTransformer } from '@/helpers/users/user.transformer.js'
import { FirmTransformer } from '@/helpers/firms/firm.transformer.js'
import { CommentTransformer } from '@/helpers/comments/comment.transformer.js'
import { LogTransformer } from '@/helpers/logs/logs.transformer.js'
import { CodeTransformer } from '@/helpers/codes/code.transformer.js'
import { GroupTransformer } from '@/helpers/groups/group.transformer.js'
import { OrderOperationTransformer } from '@/helpers/orderOperations/orderOperation.transformer.js'
import { StatusTransformer } from '@/helpers/statuses/status.transformer.js'
import { VettedStatementTransformer } from '@/helpers/vettedStatements/vettedStatement.transformer.js'
import { VettedStatementRelationshipTransformer } from '@/helpers/vettedStatementRelationships/vettedStatementRelationship.transformer.js'
import { OrderActivityTransformer } from './orderActivities/orderActivity.transformer'
import { AssetTransformer } from './assets/asset.transformer'
import { AssetNodeTransformer } from './assetNodes/assetNode.transformer'
import { PermissionTransformer } from './permissions/permission.transformer'
import { RoleTransformer } from './roles/role.transformer'
import { FileTransformer } from './files/file.transformer'
import { OrderOperationClaimLine } from './orderOperationClaimLines/orderOperationClaimLine.transformer'
import { OrderOperationClaimService } from './orderOperationClaimServices/orderOperationClaimService.transformer'
import { PlaceTransformer } from './places/place.transformer'
import { ServiceLevelTransformer } from './serviceLevels/serviceLevel.transformer'
import { TagTransformer } from './tags/tag.transformer'
import { OrderStateTransformer } from './orders/orderState.transformer'
import { NotificationTransformer } from './notifications/notification.transformer'
import { InvoiceTransformer } from './invoices/invoice.transformer'
import { PortalTransformer } from './portals/portal.transformer'
import { AttributeTransformer } from './attributes/attribute.transformer'
import { RatingTransformer } from './ratings/rating.transformer'
import { FirmUserTransformer } from './firmUsers/firmUser.transformer'
import { AssociatedTransformer } from '@/helpers/associated/associated.transformer'
import { FirmFirmTransformer } from './firmFirms/firmFirm.transformer'
import { ContractTransformer } from './contracts/contract.transformer'
import { TerminologyTransformer } from './terminologies/teminology.transformer';
import { TemplateTransformer } from './templates/template.transformer';
import { BatchTransformer } from './batch/batch.transformer';
import { TeleportationTransformer } from './teleportations/teleportation.transformer';
import { MaintenanceTaskTransformer } from './maintenanceTasks/maintenanceTask.transformer';
import { KeyTransformer } from './keys/key.transformer';
import { TopicTransformer } from './topics/topic.transformer'

// Just a better alias...
const OrderTransformer = DataTransformer

export class JsonDenormalizer {
  static denormalize (jsonResponse) {
    if (!_.isPlainObject(jsonResponse)) return

    // For coding convenience, we'll be forcing single objects into an array
    // (we'll restore to initial object vs array of objects before return)
    let bundled = []
    const data = _.get(jsonResponse, 'data')
    const included = _.get(jsonResponse, 'included', [])

    // We should have either a single object an array of objects in data source
    if (!_.isPlainObject(data) && !_.isArray(data)) return

    // This is where we temporarily force an array for coding convenience
    const results = Array.isArray(data) ? data : [data]

    const transformers = {
      firm: FirmTransformer,
      firms: FirmTransformer,
      Firms: FirmTransformer,
      'firm-managedBy': FirmTransformer,
      forms: FormTransformer,
      "form-type": FormTypeTransformer,
      order: OrderTransformer,
      orders: OrderTransformer,
      creator: UserTransformer,
      inspector: UserTransformer,
      persons: UserTransformer,
      comments: CommentTransformer,
      logs: LogTransformer,
      codes: CodeTransformer,
      parentCodes: CodeTransformer,
      childCodes: CodeTransformer,
      groups: GroupTransformer,
      statuses: StatusTransformer,
      'vetted-order-activities': VettedStatementTransformer,
      'vetted-order-activity-relationships': VettedStatementRelationshipTransformer,
      operations: OrderOperationTransformer,
      'order-activities': OrderActivityTransformer,
      'order-operations': OrderOperationTransformer,
      assets: AssetTransformer,
      'asset-nodes': AssetNodeTransformer,
      permissions: PermissionTransformer,
      roles: RoleTransformer,
      files: FileTransformer,
      'order-operation-claim-lines': OrderOperationClaimLine,
      'order-operation-claim-services': OrderOperationClaimService,
      places: PlaceTransformer,
      'service-levels': ServiceLevelTransformer,
      tags: TagTransformer,
      region: TagTransformer,
      'order-states': OrderStateTransformer,
      notifications: NotificationTransformer,
      portals: PortalTransformer,
      invoices: InvoiceTransformer,
      attributes: AttributeTransformer,
      ratings: RatingTransformer,
      associated: AssociatedTransformer,
      'date-change-logs': LogTransformer,
      'kpi-logs': LogTransformer,
      'firm-persons': FirmUserTransformer,
      'firm-firms': FirmFirmTransformer,
      'contracts': ContractTransformer,
      'terminologies': TerminologyTransformer,
      'terminology': TerminologyTransformer,
      'templates': TemplateTransformer,
      'teleportations': TeleportationTransformer,
      batch: BatchTransformer,
      'maintenance-task': MaintenanceTaskTransformer,
      'maintenance-tasks': MaintenanceTaskTransformer,
      'keys': KeyTransformer,
      'topics': TopicTransformer,
      'test-results': TopicTransformer,
    }

    const transform = function (obj, type) {
      const transformer =
        type && transformers.hasOwnProperty(type) ? transformers[type] : false

      return transformer ? transformer.transform(obj) : obj
    }

    results.forEach(obj => {
      const type = _.get(obj, 'type')
      const relationships = _.get(obj, 'relationships', {})

      // Our transformers should be able to handle it if we don't do the following re-assembly
      // let entity = _.extend({}, {id: obj.id}, obj.attributes);
      let entity = transform(obj, type)

      let associations = {} // key => object|array like {creator: userObj} OR {order: [orderObj1, orderObj2]}

      _.forEach(relationships, (rel, k) => {
        const summary = rel.data
        // const assoc = Array.isArray(summary) ? 'hasOne' : 'hasMany';  # probably faster to simply keep checking Array.isArray()?

        // Find the linked data in the "includes" object
        // (again, we'll insist on array for looping)
        let includes = []
        let summaries = Array.isArray(summary) ? summary : [summary]

        summaries.forEach(s => {
          // Using `_.matches` iteratee shorthand here.
          // https://lodash.com/docs/4.17.11#filter
          let matches = _.filter(included, s)

          if (matches.length) {
            matches.forEach(m => {
              if (_.keys(_.get(m, 'relationships', {})).length > 0) {
                includes.push(
                  this.denormalize({
                    data: m,
                    included
                  })
                )
              } else {
                includes.push(transform(m, s.type))
              }
            })
          }
        })

        associations[k] = Array.isArray(summary) ? includes : includes.shift()
      })

      // Place relationships directly onto the entity
      // (warn if overwriting existing property)
      _.forEach(associations, (assoc, assoc_name) => {
        if (entity.hasOwnProperty(assoc_name)) {
          // window.console &&
          //   console.log(
          //     `[WARNING] Object property "${assoc_name}" replaced with relationship data.`
          //   )
        }

        entity[assoc_name] = assoc
      })

      bundled.push(entity)
    })

    // Now return data in original jsonResponse format
    // (single object vs an array of objects)
    return Array.isArray(data) ? bundled : bundled.shift()
  }
}



// WEBPACK FOOTER //
// ./src/helpers/JsonDenormalizer.js